interface CacheEntry<T> { data: T; timestamp: number }

export const HUB_PREFIX = 'hub:';

export function cacheKey(userId: string, key: string): string {
  return `${HUB_PREFIX}${userId}:${key}`;
}

export function getCache<T>(userId: string, key: string, ttlMs: number): T | null {
  try {
    const raw = localStorage.getItem(cacheKey(userId, key));
    if (!raw) return null;
    const entry: CacheEntry<T> = JSON.parse(raw);
    if (Date.now() - entry.timestamp > ttlMs) {
      localStorage.removeItem(cacheKey(userId, key));
      return null;
    }
    return entry.data;
  } catch { return null; }
}

/** Raw entry regardless of TTL, for stale-while-revalidate callers. */
export function getCacheEntry<T>(userId: string, key: string): { data: T; ageMs: number } | null {
  try {
    const raw = localStorage.getItem(cacheKey(userId, key));
    if (!raw) return null;
    const entry: CacheEntry<T> = JSON.parse(raw);
    return { data: entry.data, ageMs: Date.now() - entry.timestamp };
  } catch { return null; }
}

export function setCache<T>(userId: string, key: string, data: T): void {
  try {
    localStorage.setItem(cacheKey(userId, key), JSON.stringify({ data, timestamp: Date.now() }));
  } catch { /* storage full or unavailable */ }
}

function removeMatching(prefix: string): void {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(prefix)) doomed.push(k);
    }
    doomed.forEach((k) => localStorage.removeItem(k));
  } catch { /* ignore */ }
}

export function clearUserCache(userId: string): void { removeMatching(`${HUB_PREFIX}${userId}:`); }

/**
 * Drops named cache entries for one user. Unlike clearUserCache it never
 * touches a guest's saved data, which shares the `hub:guest:` namespace.
 */
export function forgetCache(userId: string, keys: readonly string[]): void {
  keys.forEach((k) => { try { localStorage.removeItem(cacheKey(userId, k)); } catch { /* ignore */ } });
}
export function clearAllHubKeys(): void { removeMatching(HUB_PREFIX); }

const GUEST_PREFIX = `${HUB_PREFIX}guest:`;

/** Clears every `hub:` key except the guest namespace, which only signOut is meant to wipe. */
export function clearNonGuestHubKeys(): void {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(HUB_PREFIX) && !k.startsWith(GUEST_PREFIX)) doomed.push(k);
    }
    doomed.forEach((k) => localStorage.removeItem(k));
  } catch { /* ignore */ }
}

/** One-time cleanup of keys written before namespacing existed. */
export function purgeLegacyKeys(): void {
  ['rss_cache', 'newsletter_cache', 'rss_preferences', 'newsletter_preferences', 'world_clock_preferences',
    'hub:guest:rssPrefs', 'hub:guest:newsletterPrefs', 'rss_articles_per_feed', 'newsletter_articles_per_feed',
    'newsletter_last_day_only']
    .forEach((k) => { try { localStorage.removeItem(k); } catch { /* ignore */ } });
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && (k.startsWith('weather:') || k.startsWith('stocks:') || k.startsWith('news:'))) localStorage.removeItem(k);
    }
  } catch { /* ignore */ }
}
